import React, { FC, ReactElement } from "react";
import ReactTooltip from "react-tooltip";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { TooltipPlaceE } from "../../models/enums";
import useApp from "../../utils/hooks/useApp";
import { getPlaceDirection } from "./helpers/getPlaceDirection";
import Portal from "../Portal/Portal";
import { clss } from "../../utils/helpers/styleHelpers";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import removeSpecialCharacters from "../../utils/helpers/removeSpacialCharacters";

import styles from "./tooltip.module.scss";

type TooltipPropTypes = {
  text?: string;
  place?: TooltipPlaceE;
  delayShow?: number;
  addClassName?: string;
  addTooltipClassName?: string;
  withoutTranslation?: boolean;
  borderColor?: string;
  border?: boolean;
  backgroundColor?: string;
  id?: string;
  children: any;
  isShowTooltip?: boolean;
  /** "click" or "hover" – passed to react-tooltip as event/eventOff */
  trigger?: "click" | "hover";
};

/**
 * Wrap any element to show tooltip on hover
 * */

const VIEWPORT_PADDING = 12;
const DEFAULT_TOOLTIP_WIDTH = 400;
const DEFAULT_TOOLTIP_HEIGHT = 200;

const clampTooltipToViewport = (
  position: { left: number; top: number },
  tooltipId: string | undefined
): { left: number; top: number } => {
  const tooltipEl = tooltipId ? document.getElementById(tooltipId) : null;
  const width = tooltipEl?.offsetWidth ?? DEFAULT_TOOLTIP_WIDTH;
  const height = tooltipEl?.offsetHeight ?? DEFAULT_TOOLTIP_HEIGHT;
  const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
  const maxTop = window.innerHeight - height - VIEWPORT_PADDING;

  return {
    left: Math.max(VIEWPORT_PADDING, Math.min(position.left, maxLeft)),
    top: Math.max(VIEWPORT_PADDING, Math.min(position.top, maxTop)),
  };
};

const Tooltip: FC<TooltipPropTypes> = (props) => {
  const {
    children,
    text,
    place,
    delayShow = 400,
    addClassName,
    addTooltipClassName,
    withoutTranslation,
    borderColor = "#ffff",
    border,
    backgroundColor = "#333",
    id,
    isShowTooltip = true,
    trigger,
  } = props;
  const { t } = useTranslate();
  const { isRtlDirection } = useApp();

  const tooltipId = id || removeSpecialCharacters(text as string);
  const overridePosition = isMobileOrTablet()
    ? (pos: { left: number; top: number }) =>
        clampTooltipToViewport(pos, tooltipId)
    : undefined;

  return text ? (
    <>
      <span
        data-tip={withoutTranslation ? text || "data-tip" : t(text)}
        data-for={removeSpecialCharacters(text as string) || "data-tip"}
        data-event={trigger === "click" ? "click" : undefined}
        data-event-off={trigger === "click" ? "click" : undefined}
        className={clss([styles.childrenContainer, addClassName])}
      >
        {children}
      </span>
      <Portal id="root">
        <ReactTooltip
          id={tooltipId}
          className={clss([
            styles.tooltip,
            addTooltipClassName,
            isRtlDirection ? styles.rtl : "",
          ])}
          delayHide={100}
          delayShow={delayShow}
          effect="solid"
          backgroundColor={backgroundColor}
          place={getPlaceDirection(place!, isRtlDirection)}
          multiline
          disable={isMobileOrTablet() && !isShowTooltip}
          border={border}
          borderColor={borderColor}
          overridePosition={overridePosition}
        />
      </Portal>
    </>
  ) : (
    (children as ReactElement)
  );
};

export default Tooltip;
